import React, { useState, useEffect } from 'react';
import Icon from '../AppIcon';
import Button from './Button';
import Input from './Input';
import Select from './Select';
import { Checkbox } from './Checkbox';

const SettingsModal = ({ isOpen, onClose }) => {
  const [activeTab, setActiveTab] = useState('calculation');
  const [settings, setSettings] = useState({
    // Calculation Defaults
    defaultLoanAmount: '250000',
    defaultInterestRate: '6.5',
    defaultLoanTerm: '30',
    defaultPaymentFrequency: 'monthly',
    compoundingFrequency: 'monthly',
    
    // Display Preferences
    currencySymbol: '$',
    decimalPlaces: '2',
    thousandsSeparator: ',',
    showPaymentNumber: true,
    showRemainingBalance: true,
    showPrincipalPayment: true,
    showInterestPayment: true,
    showCumulativeInterest: true,
    
    // Export Options
    defaultExportFormat: 'pdf',
    includeCharts: true,
    includeAmortizationTable: true,
    includeSummary: true,
    pageOrientation: 'portrait',
    headerText: 'Loan Amortization Schedule',
    footerText: 'Generated by AmortizationPro'
  });

  const paymentFrequencyOptions = [
    { value: 'monthly', label: 'Monthly' },
    { value: 'biweekly', label: 'Bi-weekly' },
    { value: 'weekly', label: 'Weekly' },
    { value: 'quarterly', label: 'Quarterly' },
    { value: 'annually', label: 'Annually' }
  ];

  const compoundingOptions = [
    { value: 'monthly', label: 'Monthly' },
    { value: 'daily', label: 'Daily' },
    { value: 'annually', label: 'Annually' }
  ];

  const currencyOptions = [
    { value: '$', label: 'USD ($)' },
    { value: '€', label: 'EUR (€)' },
    { value: '£', label: 'GBP (£)' },
    { value: '¥', label: 'JPY (¥)' },
    { value: '₹', label: 'INR (₹)' }
  ];

  const exportFormatOptions = [
    { value: 'pdf', label: 'PDF Document' },
    { value: 'excel', label: 'Excel Spreadsheet' },
    { value: 'csv', label: 'CSV File' }
  ];

  const orientationOptions = [
    { value: 'portrait', label: 'Portrait' },
    { value: 'landscape', label: 'Landscape' }
  ];

  const tabs = [
    { id: 'calculation', label: 'Calculation Defaults', icon: 'Calculator' },
    { id: 'display', label: 'Display Preferences', icon: 'Eye' },
    { id: 'export', label: 'Export Options', icon: 'Download' }
  ];

  useEffect(() => {
    // Load settings from localStorage on mount
    const savedSettings = localStorage.getItem('amortizationProSettings');
    if (savedSettings) {
      setSettings(prev => ({ ...prev, ...JSON.parse(savedSettings) }));
    }
  }, []);

  const handleInputChange = (field, value) => {
    setSettings(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleCheckboxChange = (field, checked) => {
    setSettings(prev => ({
      ...prev,
      [field]: checked
    }));
  };

  const handleSave = () => {
    localStorage.setItem('amortizationProSettings', JSON.stringify(settings));
    onClose();
  };

  const handleReset = () => {
    localStorage.removeItem('amortizationProSettings');
    setSettings({
      defaultLoanAmount: '250000',
      defaultInterestRate: '6.5',
      defaultLoanTerm: '30',
      defaultPaymentFrequency: 'monthly',
      compoundingFrequency: 'monthly',
      currencySymbol: '$',
      decimalPlaces: '2',
      thousandsSeparator: ',',
      showPaymentNumber: true,
      showRemainingBalance: true,
      showPrincipalPayment: true,
      showInterestPayment: true,
      showCumulativeInterest: true,
      defaultExportFormat: 'pdf',
      includeCharts: true,
      includeAmortizationTable: true,
      includeSummary: true,
      pageOrientation: 'portrait',
      headerText: 'Loan Amortization Schedule',
      footerText: 'Generated by AmortizationPro'
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black bg-opacity-50 animate-fade-in"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="relative bg-background rounded-lg shadow-elevated w-full max-w-4xl max-h-[90vh] m-4 animate-scale-in">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div className="flex items-center space-x-3">
            <div className="flex items-center justify-center w-8 h-8 bg-primary rounded-lg">
              <Icon name="Settings" size={20} color="white" />
            </div>
            <h2 className="text-xl font-semibold text-foreground">Settings</h2>
          </div>
          <Button
            variant="ghost"
            size="sm"
            iconName="X"
            onClick={onClose}
            className="text-text-secondary hover:text-foreground"
          />
        </div>

        {/* Content */}
        <div className="flex h-[calc(90vh-140px)]">
          {/* Sidebar Tabs */}
          <div className="w-64 border-r border-border bg-surface">
            <nav className="p-4 space-y-2">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`w-full flex items-center space-x-3 px-3 py-2 rounded-md text-sm font-medium transition-colors duration-200 ${
                    activeTab === tab.id
                      ? 'bg-primary text-primary-foreground'
                      : 'text-text-secondary hover:text-foreground hover:bg-muted'
                  }`}
                >
                  <Icon name={tab.icon} size={16} />
                  <span>{tab.label}</span>
                </button>
              ))}
            </nav>
          </div>

          {/* Tab Content */}
          <div className="flex-1 overflow-y-auto">
            <div className="p-6">
              {activeTab === 'calculation' && (
                <div className="space-y-6">
                  <div>
                    <h3 className="text-lg font-medium text-foreground mb-4">Default Calculation Values</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <Input
                        label="Default Loan Amount"
                        type="number"
                        value={settings.defaultLoanAmount}
                        onChange={(e) => handleInputChange('defaultLoanAmount', e.target.value)}
                        placeholder="250000"
                      />
                      <Input
                        label="Default Interest Rate (%)"
                        type="number"
                        step="0.01"
                        value={settings.defaultInterestRate}
                        onChange={(e) => handleInputChange('defaultInterestRate', e.target.value)}
                        placeholder="6.5"
                      />
                      <Input
                        label="Default Loan Term (Years)"
                        type="number"
                        value={settings.defaultLoanTerm}
                        onChange={(e) => handleInputChange('defaultLoanTerm', e.target.value)}
                        placeholder="30"
                      />
                      <Select
                        label="Default Payment Frequency"
                        options={paymentFrequencyOptions}
                        value={settings.defaultPaymentFrequency}
                        onChange={(value) => handleInputChange('defaultPaymentFrequency', value)}
                      />
                      <Select
                        label="Compounding Frequency"
                        options={compoundingOptions}
                        value={settings.compoundingFrequency}
                        onChange={(value) => handleInputChange('compoundingFrequency', value)}
                        className="md:col-span-2"
                      />
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'display' && (
                <div className="space-y-6">
                  <div>
                    <h3 className="text-lg font-medium text-foreground mb-4">Number Formatting</h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <Select
                        label="Currency Symbol"
                        options={currencyOptions}
                        value={settings.currencySymbol}
                        onChange={(value) => handleInputChange('currencySymbol', value)}
                      />
                      <Input
                        label="Decimal Places"
                        type="number"
                        min="0"
                        max="4"
                        value={settings.decimalPlaces}
                        onChange={(e) => handleInputChange('decimalPlaces', e.target.value)}
                      />
                      <Input
                        label="Thousands Separator"
                        type="text"
                        maxLength="1"
                        value={settings.thousandsSeparator}
                        onChange={(e) => handleInputChange('thousandsSeparator', e.target.value)}
                      />
                    </div>
                  </div>

                  <div>
                    <h3 className="text-lg font-medium text-foreground mb-4">Table Columns</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <Checkbox
                        label="Show Payment Number"
                        checked={settings.showPaymentNumber}
                        onChange={(e) => handleCheckboxChange('showPaymentNumber', e.target.checked)}
                      />
                      <Checkbox
                        label="Show Remaining Balance"
                        checked={settings.showRemainingBalance}
                        onChange={(e) => handleCheckboxChange('showRemainingBalance', e.target.checked)}
                      />
                      <Checkbox
                        label="Show Principal Payment"
                        checked={settings.showPrincipalPayment}
                        onChange={(e) => handleCheckboxChange('showPrincipalPayment', e.target.checked)}
                      />
                      <Checkbox
                        label="Show Interest Payment"
                        checked={settings.showInterestPayment}
                        onChange={(e) => handleCheckboxChange('showInterestPayment', e.target.checked)}
                      />
                      <Checkbox
                        label="Show Cumulative Interest"
                        checked={settings.showCumulativeInterest}
                        onChange={(e) => handleCheckboxChange('showCumulativeInterest', e.target.checked)}
                        className="md:col-span-2"
                      />
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'export' && (
                <div className="space-y-6">
                  <div>
                    <h3 className="text-lg font-medium text-foreground mb-4">Export Settings</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <Select
                        label="Default Export Format"
                        options={exportFormatOptions}
                        value={settings.defaultExportFormat}
                        onChange={(value) => handleInputChange('defaultExportFormat', value)}
                      />
                      <Select
                        label="Page Orientation"
                        options={orientationOptions}
                        value={settings.pageOrientation}
                        onChange={(value) => handleInputChange('pageOrientation', value)}
                      />
                    </div>
                  </div>

                  <div>
                    <h3 className="text-lg font-medium text-foreground mb-4">Include in Export</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <Checkbox
                        label="Include Charts"
                        checked={settings.includeCharts}
                        onChange={(e) => handleCheckboxChange('includeCharts', e.target.checked)}
                      />
                      <Checkbox
                        label="Include Amortization Table"
                        checked={settings.includeAmortizationTable}
                        onChange={(e) => handleCheckboxChange('includeAmortizationTable', e.target.checked)}
                      />
                      <Checkbox
                        label="Include Summary"
                        checked={settings.includeSummary}
                        onChange={(e) => handleCheckboxChange('includeSummary', e.target.checked)}
                      />
                    </div>
                  </div>

                  <div>
                    <h3 className="text-lg font-medium text-foreground mb-4">Document Headers</h3>
                    <div className="space-y-4">
                      <Input
                        label="Header Text"
                        type="text"
                        value={settings.headerText}
                        onChange={(e) => handleInputChange('headerText', e.target.value)}
                        placeholder="Loan Amortization Schedule"
                      />
                      <Input
                        label="Footer Text"
                        type="text"
                        value={settings.footerText}
                        onChange={(e) => handleInputChange('footerText', e.target.value)}
                        placeholder="Generated by AmortizationPro"
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-border bg-surface">
          <Button
            variant="outline"
            onClick={handleReset}
            iconName="RotateCcw"
            iconPosition="left"
          >
            Reset to Defaults
          </Button>
          <div className="flex items-center space-x-3">
            <Button
              variant="outline"
              onClick={onClose}
            >
              Cancel
            </Button>
            <Button
              variant="default"
              onClick={handleSave}
              iconName="Check"
              iconPosition="left"
            >
              Save Settings
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SettingsModal;